/**
 * useCalendarSets Hook
 *
 * Manages calendar sets (named visibility presets) stored in CalendarSettings.
 * Provides CRUD operations and set activation/deactivation.
 */

import { useCallback } from 'react';
import type { CalendarCollection, CalendarSettings, CalendarSet } from '../types';

interface UseCalendarSetsOptions {
  settings: CalendarSettings;
  updateSettings: (patch: Partial<CalendarSettings>) => void;
  calendars: CalendarCollection[];
  toggleVisibility: (id: string) => Promise<void>;
}

export function useCalendarSets({
  settings,
  updateSettings,
  calendars,
  toggleVisibility,
}: UseCalendarSetsOptions) {
  const { calendarSets, activeCalendarSetId } = settings;

  const createSet = useCallback(
    (name: string, calendarIds: string[]) => {
      const newSet: CalendarSet = { id: crypto.randomUUID(), name, calendarIds };
      updateSettings({ calendarSets: [...calendarSets, newSet] });
      return newSet;
    },
    [calendarSets, updateSettings],
  );

  const renameSet = useCallback(
    (id: string, name: string) => {
      updateSettings({
        calendarSets: calendarSets.map((s) => (s.id === id ? { ...s, name } : s)),
      });
    },
    [calendarSets, updateSettings],
  );

  const deleteSet = useCallback(
    (id: string) => {
      updateSettings({
        calendarSets: calendarSets.filter((s) => s.id !== id),
        activeCalendarSetId: activeCalendarSetId === id ? null : activeCalendarSetId,
      });
    },
    [calendarSets, activeCalendarSetId, updateSettings],
  );

  const updateSetCalendars = useCallback(
    (id: string, calendarIds: string[]) => {
      updateSettings({
        calendarSets: calendarSets.map((s) => (s.id === id ? { ...s, calendarIds } : s)),
      });
    },
    [calendarSets, updateSettings],
  );

  const activateSet = useCallback(
    async (id: string) => {
      const set = calendarSets.find((s) => s.id === id);
      if (!set) return;

      // Toggle visibility for each calendar to match the set
      const promises = calendars.map((cal) => {
        const shouldBeVisible = set.calendarIds.includes(cal.id);
        if (cal.isVisible !== shouldBeVisible) {
          return toggleVisibility(cal.id);
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      updateSettings({ activeCalendarSetId: id });
    },
    [calendarSets, calendars, toggleVisibility, updateSettings],
  );

  const deactivateSet = useCallback(() => {
    updateSettings({ activeCalendarSetId: null });
  }, [updateSettings]);

  const saveCurrentAsSet = useCallback(
    (name: string) => {
      const visibleIds = calendars.filter((c) => c.isVisible).map((c) => c.id);
      return createSet(name, visibleIds);
    },
    [calendars, createSet],
  );

  return {
    calendarSets,
    activeCalendarSetId,
    createSet,
    renameSet,
    deleteSet,
    updateSetCalendars,
    activateSet,
    deactivateSet,
    saveCurrentAsSet,
  };
}
